import { useSuspenseQuery } from '@tanstack/react-query';

import Api from 'api/Api';
import type { BestAvailableVehicleImageData } from 'api/GraphQL/queries/getBestAvailableVehicleImage';

const VEHICLE_GET_IMAGE_QUERY_KEY = 'VEHICLE_GET_IMAGE';

export type ImageQueryOptions = {
  year: number;
  make: string;
  model: string;
  trim: string;
  color?: string;
  isMobile?: boolean;
};

const useImageQuery = ({
  year,
  make,
  model,
  trim,
  color = '',
  isMobile = false
}: ImageQueryOptions) => {
  const hasRequiredFields = !!year && !!make && !!model && !!trim;
  return useSuspenseQuery<BestAvailableVehicleImageData, Error, string>({
    queryKey: [VEHICLE_GET_IMAGE_QUERY_KEY, year, make, model, trim, color, isMobile],
    queryFn: ({ signal }) =>
      !hasRequiredFields
        ? Promise.resolve({ bestAvailableVehicleImage: null })
        : Api.vehicleBff.getBestAvailableImage(
            {
              year,
              make,
              model,
              trim: trim || null,
              color: color || null
            },
            signal
          ),
    gcTime: 1000 * 60 * 60, // one hour
    refetchOnMount: false,
    select: (res: BestAvailableVehicleImageData) => {
      const imageData = res?.bestAvailableVehicleImage;
      if (!imageData || imageData.isGeneric) return '';
      return isMobile ? imageData.mobileImageUrl ?? '' : imageData.desktopImageUrl ?? '';
    },
    staleTime: 1000 * 60 * 30 // 30 minutes
  });
};

export default useImageQuery;
